// SettingsPage — Account security & preferences
// Layout:
//   - Top nav: < Back | Settings
//   - Section groups: Security, Notifications, Display, Danger zone

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TierPill } from '../../components/Brand';
import { DEMO_CHALLENGE } from '../../data/platforms';

export function SettingsPage() {
  const nav = useNavigate();
  const userTier = DEMO_CHALLENGE.tier;
  const [emailNotifs, setEmailNotifs] = useState(true);
  const [pushNotifs, setPushNotifs] = useState(true);
  const [smsNotifs, setSmsNotifs] = useState(false);
  const [botAlerts, setBotAlerts] = useState(true);
  const [tradeAlerts, setTradeAlerts] = useState(true);
  const [twoFA, setTwoFA] = useState(false);

  return (
    <div className="min-h-screen bg-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Settings</div>
        <div className="w-9" />
      </div>

      {/* Account section */}
      <h2 className="mt-4 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Account</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <Row icon="👤" label="Profile" sub="Brandon · demo@funderpro.live" onClick={() => nav('/portal/profile')} />
        <Row icon="🏷️" label="Tier"><TierPill tier={userTier} /></Row>
        <Row icon="💳" label="Payment methods" sub="Visa •••• 4242" onClick={() => nav('/portal/wallet/methods')} />
        <Row icon="📍" label="Address" sub="123 Main St, San Gwann, Malta" />
      </div>

      {/* Security */}
      <h2 className="mt-6 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Security</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <Row icon="🔑" label="Change password" onClick={() => alert('Change password flow')} />
        <Row icon="📱" label="Two-factor authentication" sub={twoFA ? 'Enabled · Authenticator app' : 'Off — recommended'} toggle value={twoFA} onToggle={setTwoFA} />
        <Row icon="🖥️" label="Active sessions" sub="2 devices" onClick={() => alert('Sessions list')} />
        <Row icon="📜" label="Login history" onClick={() => alert('Login history')} last />
      </div>

      {/* Notifications */}
      <h2 className="mt-6 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Notifications</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <Row icon="📧" label="Email" sub="bot, challenge, payments" toggle value={emailNotifs} onToggle={setEmailNotifs} />
        <Row icon="🔔" label="Push" sub="real-time" toggle value={pushNotifs} onToggle={setPushNotifs} />
        <Row icon="💬" label="SMS" sub="Only critical" toggle value={smsNotifs} onToggle={setSmsNotifs} />
        <Row icon="🤖" label="Bot trade alerts" sub="Every entry/exit" toggle value={botAlerts} onToggle={setBotAlerts} />
        <Row icon="📈" label="Challenge alerts" sub="Daily loss, profit target" toggle value={tradeAlerts} onToggle={setTradeAlerts} last />
      </div>

      {/* Trading */}
      <h2 className="mt-6 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Trading</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <Row icon="🛑" label="EOD auto-flatten" sub="Enabled · flat at 16:55 ET" />
        <Row icon="📅" label="Earnings calendar" sub="Restrict new positions 2 min before/after" />
        <Row icon="💵" label="Default account size" sub="$50,000" last />
      </div>

      {/* Display */}
      <h2 className="mt-6 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Display</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        <Row icon="🌐" label="Language" sub="English (US)" />
        <Row icon="💱" label="Currency" sub="USD" />
        <Row icon="🎨" label="Theme" sub="Light" last />
      </div>

      {/* Danger zone */}
      <h2 className="mt-6 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Danger zone</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-[#FF3B30]/30 bg-white">
        <button onClick={() => alert('Sign out')} className="flex w-full items-center justify-between px-4 py-3 text-left active:bg-[#FF3B30]/5">
          <span className="text-[14px] font-semibold text-tf-loss">Sign out</span>
          <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <button onClick={() => alert('Delete account')} className="flex w-full items-center justify-between border-t border-[#FF3B30]/30 px-4 py-3 text-left active:bg-[#FF3B30]/5">
          <span className="text-[14px] font-semibold text-tf-loss">Delete account</span>
          <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      <p className="mt-4 text-center text-[11px] text-tf-text-3">Funder Pro · v1.0.0</p>
    </div>
  );
}

function Row({ icon, label, sub, children, toggle, value, onToggle, onClick, last }: { icon: string; label: string; sub?: string; children?: React.ReactNode; toggle?: boolean; value?: boolean; onToggle?: (v: boolean) => void; onClick?: () => void; last?: boolean }) {
  const Wrap: any = onClick || toggle ? 'button' : 'div';
  return (
    <Wrap
      onClick={toggle ? () => onToggle?.(!value) : onClick}
      className={['flex w-full items-center gap-3 border-tf-border px-4 py-3 text-left', last ? '' : 'border-b'].join(' ')}
    >
      <span className="text-[18px]">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-medium text-tf-text-1">{label}</div>
        {sub && <div className="text-[12px] text-tf-text-3">{sub}</div>}
        {children && <div className="mt-0.5">{children}</div>}
      </div>
      {toggle ? (
        <span className={['relative inline-block h-7 w-12 rounded-full transition', value ? 'bg-[#34C759]' : 'bg-[#E5E5EA]'].join(' ')}>
          <span className={['absolute top-0.5 h-6 w-6 rounded-full bg-white transition-all', value ? 'left-[22px]' : 'left-0.5'].join(' ')} />
        </span>
      ) : (
        <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
      )}
    </Wrap>
  );
}
